import { 
  ApolloClient, 
  InMemoryCache, 
  ApolloProvider,
  ApolloLink
} from "@apollo/client";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";


// Create an upload link for both file uploads and regular queries
const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_GRAPHQL_API_URL,
});
console.log("GraphQL API URL:", process.env.REACT_APP_GRAPHQL_API_URL);


// Matches backend messages that mean "the token itself is missing/invalid/expired"
// (as opposed to "Unauthorized: <FEATURE> ... required", which is a permission
// problem that re-logging-in won't fix). Kept broad but specific to token issues.
const TOKEN_AUTH_ERROR = /authentication token is required|jwt (expired|malformed|not active)|invalid (token|signature)|token expired/i;

// Clear the stored session and send the user to /login exactly once. This lets a
// stale or cross-environment token (e.g. one signed by staging while the app now
// points at localhost) self-heal instead of leaving the app stuck on failing
// auth — isValidUser() only checks expiry, not whether *this* backend can verify
// the signature, so without this the user would never be bounced to re-login.
const forceReLogin = () => {
  if (typeof window === "undefined") return;
  // Don't loop while already on the login page (login errors surface here too).
  if (window.location.pathname.startsWith("/login")) return;
  // Only act if we thought we were authenticated.
  if (!localStorage.getItem("token")) return;

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("features");

  const redirect = encodeURIComponent(
    window.location.pathname + window.location.search
  );
  window.location.replace(`/login?redirect=${redirect}`);
};

// Handle errors
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error("[GraphQL error]:", { message, locations, path });
      if (TOKEN_AUTH_ERROR.test(message)) {
        forceReLogin();
      }
    });
  }
  if (networkError) {
    console.error("[Network error]:", networkError);
    // 401 from the transport layer also means the token is no longer accepted.
    if ("statusCode" in networkError && networkError.statusCode === 401) {
      forceReLogin();
    }
  }
});

// Set up authentication link to include the token in headers
const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

// Combine links: error handling -> auth -> upload (terminating)
const combinedLink = ApolloLink.from([
  errorLink,
  authLink,
  uploadLink,
]);

// Create Apollo Client
const apolloClient = new ApolloClient({
  link: combinedLink,
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          // ...custom field policies if needed
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
    query: {
      fetchPolicy: "network-only",
    },
    mutate: {
      errorPolicy: "all",
    },
  },
});

export { apolloClient, ApolloProvider };
